/**
 * 파일명: clientsPartners.js
 * 작성자: 김준형
 * 작성일자: 26.08.10
 * 설명: Landing - Clients and Partners
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import CountUp from "components/common/countUp";
import {
  REVEAL_DURATION_MS,
  SectionRevealContext,
} from "components/landing/sectionReveal";
import style from "css/landing.module.css";

const COMPANY_CNT = 89;
const USER_CNT = 568676;

/*
  숫자 카운트
*/
const COUNT_STAGGER = 0;
const COUNT_DURATION = REVEAL_DURATION_MS - COUNT_STAGGER;

const COUNT_EASING = (ratio) =>
  ratio < 0.5
    ? 4 * ratio * ratio * ratio
    : 1 - Math.pow(-2 * ratio + 2, 3) / 2;

// 파트너 로고 리스트
const PARTNER_LIST = [
  {
    key: "neat",
    img: "img/landing/partner/neat-partner.png",
    alt: "Neat",
    grade: "Premier Partner",
  },
  {
    key: "zoom",
    img: "img/landing/partner/zoom-partner.png",
    alt: "Zoom",
    grade: "Gold Partner",
  },
  {
    key: "hp",
    img: "img/landing/partner/hp-partner.png",
    alt: "HP",
    grade: "Business Partner",
  },
  {
    key: "pexip",
    img: "img/landing/partner/pexip-partner.png",
    alt: "Pexip",
    grade: "Silver Partner",
  },
];

/*
  클라이언트 로고
*/
const CLIENT_ROW_1 = [
  { file: "sk-hynix.png", alt: "SK하이닉스" },
  { file: "lg-dsplay.png", alt: "LG디스플레이" },
  { file: "sansungLife.png", alt: "삼성생명" },
  { file: "harim.png", alt: "하림" },
  { file: "hankook.png", alt: "한국타이어" },
  { file: "cj.png", alt: "CJ" },
  { file: "daesang.png", alt: "대상" },
  { file: "coupang.png", alt: "쿠팡" },
  { file: "samsung-bioepis.png", alt: "삼성바이오에피스" },
  { file: "hyundai-motor.png", alt: "현대자동차그룹" },
  { file: "sk-shieldus.png", alt: "SK쉴더스" },
  { file: "krafton.png", alt: "크래프톤" },
  { file: "ouabro.png", alt: "우아한형제들" },
  { file: "dousan.png", alt: "두산건설" },
  { file: "sansung-seoul.png", alt: "삼성서울병원" },
];

const CLIENT_ROW_2 = [
  { file: "amny.png", alt: "대한민국 육군" },
  { file: "lx.png", alt: "LX" },
  { file: "ministry.png", alt: "외교부" },
  { file: "koreabank.png", alt: "한국은행" },
  { file: "presidential-security.png", alt: "대통령경호처" },
  { file: "tax.png", alt: "국세청" },
  { file: "junbook.png", alt: "전라북도청" },
  { file: "lh-1.png", alt: "LH 한국토지주택공사" },
  { file: "hanyang.png", alt: "한양대학교" },
  { file: "oungang.png", alt: "원광디지털대학교" },
  { file: "busan-uni.png", alt: "부산디지털대학교" },
];

function ClientsPartners() {
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const cpRef = useRef(null);
  const isRevealed = useContext(SectionRevealContext);

  // 0 = 아직 센 적 없음, n = n번째 재생. 값이 바뀌면 CountUp 이 새로 마운트돼 0부터 센다.
  const [countRun, setCountRun] = useState(0);

  useEffect(() => {
    if (!isRevealed) return undefined;

    const timer = setTimeout(
      () => setCountRun((run) => run + 1),
      COUNT_STAGGER,
    );

    return () => clearTimeout(timer);
  }, [isRevealed]);

  /*
    사용기관, 연 사용 인원 숫자 카운트
  */
  const renderCount = (target) =>
    countRun > 0 ? (
      <CountUp
        key={countRun}
        init
        ref={cpRef}
        target={target}
        duration={COUNT_DURATION}
        easing={COUNT_EASING}
      />
    ) : (
      "0"
    );

  const renderClientTile = (client, index, isClone) => (
    <div
      className={style.cp_client_tile}
      key={`${isClone ? "clone" : "origin"}-${client.file}-${index}`}
      aria-hidden={isClone ? "true" : undefined}
    >
      <img
        className={style.cp_client_logo}
        src={`${process.env.REACT_APP_CLIENT_URL}img/landing/client/${client.file}`}
        alt={isClone ? "" : client.alt}
      />
    </div>
  );

  // 1행은 좌측, 2행은 우측으로 흐른다
  const renderClientTrack = (list, isSecondRow) => (
    <div className={style.cp_client_marquee}>
      <div
        className={`${style.cp_client_track} ${
          isSecondRow ? style.cp_client_track_reverse : ""
        }`}
      >
        {list.map((client, index) => renderClientTile(client, index, false))}
        {list.map((client, index) => renderClientTile(client, index, true))}
      </div>
    </div>
  );

  return (
    <div
      className={style.cp_wrap}
      ref={cpRef}
    >
      <img
        className={style.cp_bg_img}
        src={`${process.env.REACT_APP_CLIENT_URL}img/landing/history/cb_bg${
          isMobile ? "_m" : ""
        }.png`}
        alt=""
      />

      <div className={style.cp_head}>
        <div className={style.cp_head_text}>
          <h2 className={style.cp_title}>
            {isMobile ? (
              <>
                Clients
                <br />
                and Partners
              </>
            ) : (
              "Clients and Partners"
            )}
          </h2>
          <p className={style.cp_desc}>
            {isMobile ? (
              <>
                2013년부터 리브테크놀로지와 많은
                <br />
                기업/기관/학교들이 함께했습니다.
              </>
            ) : (
              "2013년부터 리브테크놀로지와 많은 기업/기관/학교들이 함께했습니다."
            )}
          </p>
        </div>

        <div className={style.cp_stat_group}>
          <div className={style.cp_stat}>
            <p className={style.cp_stat_num}>
              {renderCount(COMPANY_CNT)}
              <span className={style.cp_stat_plus}>+</span>
            </p>
            <div className={style.cp_stat_label}>사용 기관</div>
          </div>
          <div className={style.cp_stat}>
            <p className={style.cp_stat_num}>
              {renderCount(USER_CNT)}
              <span className={style.cp_stat_plus}>+</span>
            </p>
            <div className={style.cp_stat_label}>연 사용 인원</div>
          </div>
        </div>
      </div>

      <div className={style.cp_section_label}>
        <span className={style.cp_label_dot} />
        Partners
      </div>
      <div className={style.cp_partner_list}>
        {PARTNER_LIST.map((partner) => (
          <div
            className={style.cp_partner_card}
            key={partner.key}
          >
            <div className={style.cp_partner_logo_wrap}>
              <img
                className={style.cp_partner_logo}
                src={`${process.env.REACT_APP_CLIENT_URL}${partner.img}`}
                alt={`${partner.alt} ${partner.grade}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className={style.cp_section_label}>
        <span className={style.cp_label_dot} />
        Clients
      </div>
      {renderClientTrack(CLIENT_ROW_1, false)}
      {renderClientTrack(CLIENT_ROW_2, true)}
    </div>
  );
}

export default ClientsPartners;
